import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Container from 'react-bootstrap/Container';
import Card from 'react-bootstrap/Card';
import ProgressBar from 'react-bootstrap/ProgressBar';
import Alert from 'react-bootstrap/Alert';
import ContactInfoStep from './ContactInfoStep';
import WorkLocationsStep from './WorkLocationsStep';
import EquipmentExperienceStep from './EquipmentExperienceStep';
import ReviewStep from './ReviewStep';
import { fetchCsrfToken, submitSubcontractorRegistration } from '../../api/subcontractor';
import './SubcontractorRegistration.css';

const STEPS = [
  { id: 1, title: 'Contact Information' },
  { id: 2, title: 'Work Locations' },
  { id: 3, title: 'Equipment Experience' },
  { id: 4, title: 'Review & Submit' },
];

const initialFormData = {
  // Contact Info
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  company_name: '',
  address: '',
  city: '',
  state: '',
  zip_code: '',
  // Work Locations
  work_locations: [],
  // Equipment Experience
  equipment_experience: {},
  years_in_business: 0,
  has_insurance: false,
  insurance_details: '',
  additional_notes: '',
};

function SubcontractorRegistration() {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  useEffect(() => {
    fetchCsrfToken().catch(console.error);
  }, []);

  const updateFormData = (data) => {
    setFormData((prev) => ({ ...prev, ...data }));
    setErrors({});
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.first_name.trim()) newErrors.first_name = 'First name is required';
      if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required';
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Invalid email format';
      }
      if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
      if (!formData.address.trim()) newErrors.address = 'Address is required';
      if (!formData.city.trim()) newErrors.city = 'City is required';
      if (!formData.state.trim()) newErrors.state = 'State is required';
      if (!formData.zip_code.trim()) newErrors.zip_code = 'ZIP code is required';
    }

    if (step === 2) {
      if (formData.work_locations.length === 0) {
        newErrors.work_locations = 'Please select at least one work location';
      }
    }

    if (step === 3) {
      const hasExperience = Object.values(formData.equipment_experience).some(
        (exp) => exp.hasExperience
      );
      if (!hasExperience) {
        newErrors.equipment_experience = 'Please indicate experience with at least one equipment type';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, STEPS.length));
      window.scrollTo(0, 0);
    }
  };

  const handlePrevious = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const submissionData = {
        ...formData,
        equipment_experience: Object.fromEntries(
          Object.entries(formData.equipment_experience)
            .filter(([, value]) => value.hasExperience)
            .map(([key, value]) => [key, { years: value.years, details: value.details }])
        ),
      };

      await submitSubcontractorRegistration(submissionData);
      setSubmitSuccess(true);
    } catch (error) {
      setSubmitError(error.message || 'Failed to submit registration. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartOver = () => {
    setFormData(initialFormData);
    setCurrentStep(1);
    setSubmitSuccess(false);
    setSubmitError(null);
    setErrors({});
    setTermsAccepted(false);
  };

  const progress = (currentStep / STEPS.length) * 100;

  if (submitSuccess) {
    return (
      <Container className="py-4">
        <Card className="shadow-sm">
          <Card.Body className="text-center py-5">
            <div className="text-success mb-3">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
              </svg>
            </div>
            <h3 className="mb-3">Registration Submitted Successfully!</h3>
            <p className="text-muted mb-4">
              Thank you for registering as a subcontractor. We will review your application and contact you soon.
            </p>
            <button className="btn btn-primary" onClick={handleStartOver}>
              Submit Another Registration
            </button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Helmet>
        <title>Subcontractor Registration | Join the United Arborist Network</title>
        <meta name="description" content="Register as a tree care subcontractor with United Arborist for free. Connect with established companies seeking skilled tree climbers, equipment operators, and CDL drivers nationwide." />
        <meta name="keywords" content="tree service subcontractor registration, arborist subcontractor signup, tree climber jobs, equipment operator registration, CDL driver tree service, tree care contractor opportunities, freelance arborist, independent tree contractor" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.unitedarborist.com/subcontractor-registration" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="United Arborist" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:title" content="Subcontractor Registration | Join the United Arborist Network" />
        <meta property="og:description" content="Register as a tree care subcontractor with United Arborist and connect with companies seeking skilled climbers, equipment operators, and CDL drivers." />
        <meta property="og:url" content="https://www.unitedarborist.com/subcontractor-registration" />
        <meta property="og:image" content="https://www.unitedarborist.com/logo.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Subcontractor Registration | Join the United Arborist Network" />
        <meta name="twitter:description" content="Register as a tree care subcontractor and connect with companies seeking skilled climbers, equipment operators, and CDL drivers." />
        <meta name="twitter:image" content="https://www.unitedarborist.com/logo.jpg" />
      </Helmet>
      <Card className="shadow-sm">
        <Card.Header className="bg-white">
          <h4 className="mb-3">Subcontractor Registration</h4>
          <div className="step-indicators d-none d-md-flex justify-content-between mb-3">
            {STEPS.map((step) => (
              <div
                key={step.id}
                className={`step-indicator ${currentStep >= step.id ? 'active' : ''} ${
                  currentStep === step.id ? 'current' : ''
                }`}
              >
                <span className="step-number">{step.id}</span>
                <span className="step-title">{step.title}</span>
              </div>
            ))}
          </div>
          <div className="d-md-none mb-2">
            <small className="text-muted">
              Step {currentStep} of {STEPS.length}: {STEPS[currentStep - 1].title}
            </small>
          </div>
          <ProgressBar now={progress} className="mb-0" style={{ height: '4px' }} />
        </Card.Header>

        <Card.Body>
          {submitError && (
            <Alert variant="danger" dismissible onClose={() => setSubmitError(null)}>
              {submitError}
            </Alert>
          )}

          {currentStep === 1 && (
            <ContactInfoStep
              data={formData}
              errors={errors}
              onChange={updateFormData}
            />
          )}

          {currentStep === 2 && (
            <WorkLocationsStep
              selectedStates={formData.work_locations}
              error={errors.work_locations}
              onChange={(locations) => updateFormData({ work_locations: locations })}
            />
          )}

          {currentStep === 3 && (
            <EquipmentExperienceStep
              data={formData}
              errors={errors}
              onChange={updateFormData}
            />
          )}

          {currentStep === 4 && (
            <ReviewStep
              data={formData}
              termsAccepted={termsAccepted}
              onTermsChange={setTermsAccepted}
            />
          )}
        </Card.Body>

        <Card.Footer className="bg-white">
          <div className="d-flex justify-content-between">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={handlePrevious}
              disabled={currentStep === 1}
            >
              Previous
            </button>

            {currentStep < STEPS.length ? (
              <button type="button" className="btn btn-primary" onClick={handleNext}>
                Next
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-success"
                onClick={handleSubmit}
                disabled={isSubmitting || !termsAccepted}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Registration'}
              </button>
            )}
          </div>
        </Card.Footer>
      </Card>
    </Container>
  );
}

export default SubcontractorRegistration;
